/**
 * ChatMessage - Composant pour afficher les messages du chatbot avec formatage riche
 * Convertit le markdown basique en HTML lisible
 * Optimisé pour les petits écrans avec tableaux scrollables
 */
import React from 'react';
import { Bot, User } from 'lucide-react';

// Détecte si le texte contient un tableau (lignes avec | ou # Client Montant)
const detectTable = (lines) => {
  let tableStart = -1;
  let tableEnd = -1;
  let tableType = null; // 'pipe' ou 'numbered'
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    // Tableau avec pipes |
    if (line.startsWith('|') && line.endsWith('|')) {
      if (tableStart === -1) tableStart = i;
      tableEnd = i;
      tableType = 'pipe';
    }
    // Tableau numéroté (# Client Montant ou 1 SERFA 490€)
    else if (/^#\s+\w+\s+\w+/.test(line) || /^\d+\s+[A-Z]+/.test(line)) {
      if (tableStart === -1) tableStart = i;
      tableEnd = i;
      tableType = 'numbered';
    }
  }
  
  return { tableStart, tableEnd, tableType };
};

// Parse un tableau numéroté en lignes/colonnes
const parseNumberedTable = (lines) => {
  const rows = [];
  let headers = null;
  
  for (const line of lines) {
    const trimmed = line.trim();
    
    // Header row (# Client Montant Facture Date)
    if (trimmed.startsWith('#')) {
      headers = trimmed.split(/\s{2,}/).map(h => h.trim());
      continue;
    }
    
    // Data row (1 SERFA 490,00€ F20260114 14/01/2026)
    const match = trimmed.match(/^(\d+)\s+(.+)/);
    if (match) {
      const cells = match[2].split(/\s{2,}/).map(c => c.trim());
      rows.push([match[1], ...cells]);
    }
  }
  
  return { headers, rows };
};

// Composant Table scrollable
const ScrollableTable = ({ headers, rows }) => {
  if (!rows || rows.length === 0) return null;
  
  return (
    <div className="my-2 -mx-1 overflow-x-auto">
      <table className="min-w-full text-xs border-collapse">
        {headers && (
          <thead>
            <tr className="bg-amber-50 border-b border-amber-200">
              {headers.map((h, i) => (
                <th key={i} className="px-2 py-1.5 text-left font-semibold text-amber-800 whitespace-nowrap">
                  {h === '#' ? 'N°' : h}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {rows.map((row, ridx) => (
            <tr key={ridx} className="border-b border-slate-100 hover:bg-slate-50">
              {row.map((cell, cidx) => (
                <td key={cidx} className={`px-2 py-1.5 whitespace-nowrap ${cidx === 0 ? 'font-medium text-amber-600' : ''} ${cell && cell.includes('€') ? 'font-semibold text-emerald-700 tabular-nums' : ''}`}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Composant pour liste de données compacte (alternative au tableau)
const CompactDataList = ({ items }) => {
  return (
    <div className="my-2 space-y-2">
      {items.map((item, idx) => (
        <div key={idx} className="p-2 bg-slate-50 rounded-lg border border-slate-100">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-medium text-amber-600 shrink-0">#{idx + 1}</span>
            <span className="font-semibold text-emerald-700 text-sm tabular-nums">{item.montant}</span>
          </div>
          <p className="font-medium text-slate-800 text-sm mt-1 truncate">{item.client}</p>
          <div className="flex gap-2 text-[11px] text-slate-500 mt-0.5">
            {item.facture && <span>{item.facture}</span>}
            {item.date && <span>• {item.date}</span>}
          </div>
        </div>
      ))}
    </div>
  );
};

// Parse invoice list from text (format: "1 SERFA 1 490,00€ F20260114-11452 14/01/2026")
const parseInvoiceList = (text) => {
  const items = [];
  const lines = text.split('\n');
  
  for (const line of lines) {
    // Pattern: numéro client montant€ facture date
    const match = line.match(/^\d+\s+([A-Z][A-Z\s]+?)\s+(\d[\d\s,.]*€)\s+(F\d{8}-\d+)\s+(\d{2}\/\d{2}\/\d{4})/i);
    if (match) {
      items.push({
        client: match[1].trim(),
        montant: match[2].trim(),
        facture: match[3].trim(),
        date: match[4].trim()
      });
    }
  }
  
  return items;
};

// Fonction pour formater le texte markdown en éléments React
const formatMessage = (text) => {
  if (!text) return null;
  
  const lines = text.split('\n');
  const elements = [];
  let listItems = [];
  let inList = false;
  let tableBuffer = [];
  let inTable = false;
  
  const flushList = () => {
    if (listItems.length > 0) {
      elements.push(
        <ul key={`list-${elements.length}`} className="my-2 space-y-1">
          {listItems}
        </ul>
      );
      listItems = [];
    }
    inList = false;
  };
  
  const flushTable = () => {
    if (tableBuffer.length > 0) {
      // Check if it looks like invoice data
      const invoices = parseInvoiceList(tableBuffer.join('\n'));
      if (invoices.length > 0) {
        elements.push(
          <CompactDataList key={`data-${elements.length}`} items={invoices} />
        );
      } else {
        // Try numbered table
        const { headers, rows } = parseNumberedTable(tableBuffer);
        if (rows.length > 0) {
          elements.push(
            <ScrollableTable key={`table-${elements.length}`} headers={headers} rows={rows} />
          );
        }
      }
      tableBuffer = [];
    }
    inTable = false;
  };
  
  lines.forEach((line, idx) => {
    const trimmed = line.trim();
    
    // Skip empty lines
    if (!trimmed) {
      flushList();
      flushTable();
      return;
    }
    
    // Detect table header or numbered row
    if (/^#\s+\w+\s+\w+/.test(trimmed) || /^\d+\s+[A-Z]{2,}/.test(trimmed)) {
      flushList();
      inTable = true;
      tableBuffer.push(trimmed);
      return;
    }
    
    // Continue table if in table mode
    if (inTable && /^\d+\s+/.test(trimmed)) {
      tableBuffer.push(trimmed);
      return;
    } else if (inTable) {
      flushTable();
    }
    
    // Headers (### ou ##)
    if (trimmed.startsWith('###')) {
      flushList();
      const headerText = trimmed.replace(/^#{1,3}\s*/, '');
      elements.push(
        <h4 key={idx} className="font-semibold text-amber-700 mt-3 mb-1 text-sm">
          {formatInlineText(headerText)}
        </h4>
      );
      return;
    }
    
    if (trimmed.startsWith('##')) {
      flushList();
      const headerText = trimmed.replace(/^#{1,2}\s*/, '');
      elements.push(
        <h3 key={idx} className="font-bold text-amber-800 mt-3 mb-2">
          {formatInlineText(headerText)}
        </h3>
      );
      return;
    }
    
    if (trimmed.startsWith('#') && !trimmed.startsWith('# ') && /^#\s+\w+/.test(trimmed)) {
      // This might be a table header, handle above
    } else if (trimmed.startsWith('#')) {
      flushList();
      const headerText = trimmed.replace(/^#\s*/, '');
      elements.push(
        <h2 key={idx} className="font-bold text-lg text-amber-900 mt-3 mb-2">
          {formatInlineText(headerText)}
        </h2>
      );
      return;
    }
    
    // Horizontal rule
    if (trimmed === '---' || trimmed === '***' || trimmed === '___') {
      flushList();
      elements.push(<hr key={idx} className="my-3 border-slate-200" />);
      return;
    }
    
    // List items (- ou • ou *)
    if (/^[-•*]\s/.test(trimmed)) {
      inList = true;
      const listText = trimmed.replace(/^[-•*]\s*/, '');
      listItems.push(
        <li key={idx} className="flex items-start gap-2 text-sm">
          <span className="text-amber-500 mt-0.5 shrink-0">•</span>
          <span className="min-w-0">{formatInlineText(listText)}</span>
        </li>
      );
      return;
    }
    
    // Numbered lists (but not table rows)
    if (/^\d+[.)]\s/.test(trimmed) && !/^\d+\s+[A-Z]{2,}/.test(trimmed)) {
      inList = true;
      const match = trimmed.match(/^(\d+)[.)]\s*(.*)/);
      if (match) {
        listItems.push(
          <li key={idx} className="flex items-start gap-2 text-sm">
            <span className="text-amber-600 font-medium min-w-[1.2rem] shrink-0">{match[1]}.</span>
            <span className="min-w-0">{formatInlineText(match[2])}</span>
          </li>
        );
      }
      return;
    }
    
    // Blockquote
    if (trimmed.startsWith('>')) {
      flushList();
      const quoteText = trimmed.replace(/^>\s*/, '');
      elements.push(
        <blockquote key={idx} className="border-l-2 border-amber-400 pl-2 my-2 text-slate-600 italic bg-amber-50/50 py-1 rounded-r text-sm">
          {formatInlineText(quoteText)}
        </blockquote>
      );
      return;
    }
    
    // Table separator (|---|)
    if (/^\|[-:|\s]+\|$/.test(trimmed)) {
      return; // Skip table separators
    }
    
    // Table row with pipes
    if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
      flushList();
      const cells = trimmed.split('|').filter(c => c.trim());
      elements.push(
        <div key={idx} className="flex gap-2 py-1 border-b border-slate-100 text-xs overflow-x-auto">
          {cells.map((cell, cidx) => (
            <span key={cidx} className={`shrink-0 ${cidx === 0 ? 'font-medium min-w-[60px]' : 'min-w-[80px]'}`}>
              {formatInlineText(cell.trim())}
            </span>
          ))}
        </div>
      );
      return;
    }
    
    // Regular paragraph
    flushList();
    elements.push(
      <p key={idx} className="my-1.5 leading-relaxed text-sm">
        {formatInlineText(trimmed)}
      </p>
    );
  });
  
  // Flush remaining
  flushList();
  flushTable();
  
  return elements;
};

// Format inline text (bold, italic, code, amounts)
const formatInlineText = (text) => {
  if (!text) return null;
  
  const parts = [];
  let remaining = text;
  let keyIndex = 0;
  
  while (remaining.length > 0) {
    // Bold (**text** or __text__)
    const boldMatch = remaining.match(/^(.*?)(\*\*|__)(.+?)\2(.*)$/);
    if (boldMatch) {
      if (boldMatch[1]) parts.push(boldMatch[1]);
      parts.push(<strong key={keyIndex++} className="font-semibold text-slate-900">{boldMatch[3]}</strong>);
      remaining = boldMatch[4];
      continue;
    }
    
    // Inline code (`code`)
    const codeMatch = remaining.match(/^(.*?)`([^`]+)`(.*)$/);
    if (codeMatch) {
      if (codeMatch[1]) parts.push(codeMatch[1]);
      parts.push(
        <code key={keyIndex++} className="px-1 py-0.5 bg-slate-100 rounded text-amber-700 text-xs font-mono">
          {codeMatch[2]}
        </code>
      );
      remaining = codeMatch[3];
      continue;
    }
    
    // Format amounts (12 345,67€ or 12345.67€)
    const amountMatch = remaining.match(/^(.*?)(\d[\d\s]*(?:[,.])\d{2}\s*€)(.*)$/);
    if (amountMatch) {
      if (amountMatch[1]) parts.push(amountMatch[1]);
      parts.push(
        <span key={keyIndex++} className="font-semibold text-emerald-700 tabular-nums whitespace-nowrap">
          {amountMatch[2]}
        </span>
      );
      remaining = amountMatch[3];
      continue;
    }
    
    // Format percentages
    const percentMatch = remaining.match(/^(.*?)([-+]?\d+(?:[,.]\d+)?%)(.*)$/);
    if (percentMatch) {
      if (percentMatch[1]) parts.push(percentMatch[1]);
      const isPositive = percentMatch[2].startsWith('+');
      const isNegative = percentMatch[2].startsWith('-');
      parts.push(
        <span key={keyIndex++} className={`font-medium whitespace-nowrap ${isPositive ? 'text-emerald-600' : isNegative ? 'text-red-600' : 'text-slate-700'}`}>
          {percentMatch[2]}
        </span>
      );
      remaining = percentMatch[3];
      continue;
    }
    
    // No more matches
    parts.push(remaining);
    break;
  }
  
  return parts.length > 0 ? parts : text;
};

// Composant principal
const ChatMessage = ({ message, isUser = false }) => {
  if (isUser) {
    return (
      <div className="flex gap-2 justify-end">
        <div className="max-w-[90%] p-2.5 rounded-2xl rounded-br-sm bg-amber-500 text-white text-sm">
          <p className="whitespace-pre-wrap break-words">{message}</p>
        </div>
        <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center shrink-0">
          <User className="w-3.5 h-3.5 text-slate-600" />
        </div>
      </div>
    );
  }
  
  return (
    <div className="flex gap-2 justify-start">
      <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center shrink-0 mt-1">
        <Bot className="w-3.5 h-3.5 text-amber-600" />
      </div>
      <div className="max-w-[90%] min-w-0 p-2.5 rounded-2xl rounded-bl-sm bg-slate-50 border border-slate-100 text-slate-800 overflow-hidden">
        <div className="chat-message-content overflow-x-auto">
          {formatMessage(message)}
        </div>
      </div>
    </div>
  );
};

export default ChatMessage;
